/**
 * InlinePicklist — the row-level picklist for Slice D / PR D3 (inline edit IN
 * a list). One control for both fields on both lists: the closed state renders
 * whatever the caller passes (a status badge, an owner name) — ALWAYS the
 * stored value, never the one just clicked — and a click opens a menu of the
 * bounded options with the current one checked. Choosing an option commits
 * it at once (D3.3); choosing the current one just closes.
 *
 * Why a menu and not a native <select>: a focused native select changes its
 * value — and would fire the permanent history write — on an arrow key or a
 * wheel tick with the list still closed. A menu commits only on a click (or
 * Enter/Space) on a NAMED option after the list has been opened: two
 * deliberate gestures, nothing accidental (D3.2).
 *
 * The menu is portalled to <body> and positioned fixed, because both list
 * cards clip overflow (.k-card overflow:hidden; the leads table sits in an
 * overflow-x-auto wrapper) and an in-flow absolute menu would be cut off.
 * Being fixed, it must FOLLOW its trigger: any scroll (capture phase, any
 * scroll container) re-places the menu under the trigger; only a resize
 * closes it, because a reflow can move the trigger anywhere. A 1px
 * programmatic scroll of an over-wide card — a table whose min-content
 * exceeds its card by a sub-pixel — used to close a menu the instant it
 * opened; a menu that survives a scroll is the correct behaviour for every
 * user, not only for the test that found it.
 * The wrapper stops click propagation so a click on the control never opens
 * the row's drawer/detail, and a click on the row never reaches the control.
 *
 * Chrome cites SavedViewsBar (the D2 menus): surface-card menu on the control
 * radius, Check on the current item, hover on surface-nav-hover. No new token.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import type { PickOption } from './inlineEdit'

export interface InlinePicklistProps {
  /** The STORED value ('' when nothing is stored). */
  value: string
  options: ReadonlyArray<PickOption>
  /** What the closed control shows — the stored value's rendering. */
  children: React.ReactNode
  /** Accessible name for the trigger, e.g. "Change status for Tom Okafor". */
  label: string
  /** A write for this row is in flight: the trigger says so and is disabled. */
  busy?: boolean
  /** Fires ONLY with a value that differs from `value`. */
  onCommit: (value: string) => void
  /** Shown as one disabled line when `options` is empty (people loading /
   *  unavailable) — never a free-text fallback. */
  emptyText?: string
}

const MENU_GAP = 4

export function InlinePicklist({ value, options, children, label, busy = false, onCommit, emptyText = 'No options' }: InlinePicklistProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Place the menu under the trigger; flip above when it would run off the
  // bottom of the viewport. Measured after the menu has rendered, and again
  // on every scroll so the menu follows the trigger (see the header).
  const place = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const h = menuRef.current?.offsetHeight ?? 0
    const below = r.bottom + MENU_GAP
    const top = h > 0 && below + h > window.innerHeight && r.top - MENU_GAP - h > 0
      ? r.top - MENU_GAP - h
      : below
    setPos(p => (p && p.top === top && p.left === r.left ? p : { top, left: r.left }))
  }, [])
  useLayoutEffect(() => { if (open) place() }, [open, options.length, place])

  // Close on outside mousedown, Escape or resize. A scroll — anywhere, any
  // container, capture phase — RE-PLACES the menu instead of closing it.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus() } }
    const onScroll = () => place()
    const onResize = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, place])

  // Focus the current option once the menu HAS options — on open when they
  // are already there, or when they first arrive (a directory still loading
  // at open time opens the menu as "Loading people…"; without this the items
  // then render with nothing focused and Enter does nothing for a keyboard
  // user). Never steals focus from an option the user has already moved to.
  useEffect(() => {
    if (!open || !menuRef.current) return
    const items = menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')
    if (items.length === 0 || menuRef.current.contains(document.activeElement)) return
    const current = Array.from(items).find(el => el.getAttribute('aria-checked') === 'true') ?? items[0]
    current.focus()
  }, [open, options.length])

  function onMenuKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [])
    if (items.length === 0) return
    const i = items.findIndex(el => el === document.activeElement)
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length
    items[next].focus()
    e.preventDefault()
  }

  function choose(next: string) {
    setOpen(false)
    triggerRef.current?.focus()
    if (next !== value) onCommit(next)
  }

  return (
    <span className="inline-flex" onClick={e => e.stopPropagation()}>
      <button ref={triggerRef} type="button"
        className="inline-flex items-center gap-1 -mx-1.5 px-1.5 py-0.5 rounded-control hover:bg-surface-nav-hover transition-colors disabled:opacity-60 disabled:cursor-default"
        aria-haspopup="menu" aria-expanded={open} aria-label={label} disabled={busy}
        onClick={() => setOpen(o => !o)}>
        {children}
        {busy
          ? <span className="text-11 text-ink-faint">Saving…</span>
          : <ChevronDown size={12} className="text-ink-faint" />}
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" aria-label={label} onKeyDown={onMenuKey}
          className="bg-surface-card border border-edge-control rounded-control shadow-lg overflow-hidden py-1"
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: 60, minWidth: 180 }}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-12 text-ink-muted">{emptyText}</div>
          ) : options.map(o => {
            const current = o.value === value
            return (
              <button key={o.value} type="button" role="menuitemradio" aria-checked={current}
                className={`w-full flex items-center gap-2 px-3 py-2 text-13 text-left hover:bg-surface-nav-hover focus:outline-none focus:bg-surface-nav-hover ${current ? 'text-ink-primary' : 'text-ink-body'}`}
                onClick={() => choose(o.value)}>
                <span className="w-4 flex justify-center flex-shrink-0">{current && <Check size={13} className="text-accent" />}</span>
                <span className="flex-1 truncate">{o.label}</span>
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </span>
  )
}
